import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const PROOF_SCREENS = [{
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/d6334987-31bd-417c-9b9c-41c04b4aedf3.jpg',
  alt: 'Interactive story opening screen',
  label: 'Story',
  offset: 'translate-y-8'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/ef040569-9125-4417-a8f8-9a15b58cd0ad.jpg',
  alt: 'Interactive clinical choice screen',
  label: 'Choice',
  offset: '-translate-y-4'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/56222443-71b7-4ba3-a811-34975210598a.jpg',
  alt: 'Answer reveal and peer-response screen',
  label: 'Insight',
  offset: 'translate-y-5'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/2d2cc906-1f13-43c8-b217-0a228cac8205.jpg',
  alt: 'Takeaway and next-step screen',
  label: 'Action',
  offset: '-translate-y-8'
}] as const;
const PAYOFFS = ['Short enough to finish.', 'Interesting enough to continue.', 'Interactive enough to learn from.'] as const;
export function SlideSnakDefinition({}: SlideProps) {
  return <InteriorSlide surface data-id="element-385" data-mp="18b7rlnluurna">
      <div className="flex h-full min-h-0 flex-col lg:grid lg:grid-cols-12 lg:items-center lg:gap-12 xl:gap-16" data-id="element-386" data-mp="18aeu7d8sdeht">
        <div className="flex min-h-0 flex-col justify-center lg:col-span-5" data-id="element-387" data-mp="1w1twj60kbvt2">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-5 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-388" data-mp="202w01z1iiu0u">
            The attention problem
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(2.25rem,4.3vw,4.35rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.045em] text-[var(--fg)]" data-id="element-389" data-mp="aluzh2wkm5hb">
            HCPs don’t lack content.
            <span className="mt-2 block text-[var(--brand)]" data-id="element-390" data-mp="3ix8jubf7ehza">They lack time.</span>
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="mt-7 border-l-2 border-[var(--brand)] pl-5" data-id="element-391" data-mp="3dyxeodzusvne">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-392" data-mp="33yofqseh8w4b">
              So we created the Snak.
            </p>
            <p className="mt-2 text-[clamp(1.35rem,2vw,1.9rem)] font-bold leading-tight text-[var(--fg)]" data-id="element-393" data-mp="2uzt95f7itk0h">
              A short story you participate in.
            </p>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45,
          duration: 0.5
        }} className="mt-6 max-w-xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-394" data-mp="t8g32mtth62u">
            A Snak turns complex content into a short, engaging, gamified journey.
            HCPs don’t just read or watch—they choose, predict, react, and discover.
            Every interaction creates insight.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} className="mt-6 space-y-2" data-id="element-395" data-mp="htmw0pnucg5p">
            {PAYOFFS.map((payoff, index) => <div key={payoff} className="flex items-center gap-3" data-id="element-396" data-mp="vsmhxpjyygy6" data-mpi={index}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[10px] font-bold text-white" data-id="element-397" data-mp="vtxkxn5lbko8">
                  {index + 1}
                </span>
                <p className="text-sm font-semibold text-[var(--fg)]" data-id="element-398" data-mp="279umqbyj1z2v">{payoff}</p>
              </div>)}
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.25,
        duration: 0.7
      }} className="relative mt-8 flex min-h-0 flex-1 items-center lg:col-span-7 lg:mt-0 lg:h-full" aria-label="Examples of the Snak experience" data-id="element-399" data-mp="20sb01g132ump">
          <div className="absolute inset-x-8 inset-y-10 rounded-[2.5rem] bg-white shadow-sm" data-id="element-400" data-mp="2oo5javhr0prv" />
          <div className="relative z-10 grid w-full grid-cols-4 items-center gap-3 xl:gap-4" data-id="element-401" data-mp="2jplwksvbz9yg">
            {PROOF_SCREENS.map((screen, index) => <motion.figure key={screen.label} initial={{
            opacity: 0,
            y: 26
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45 + index * 0.1,
            duration: 0.55
          }} className={screen.offset} data-id="element-402" data-mp="2tg6su9nj82e7" data-mpi={index}>
                <img src={screen.src} alt={screen.alt} className="w-full rounded-[1.4rem] object-cover shadow-[0_18px_45px_rgba(23,32,51,0.16)]" data-id="element-403" data-mp="33cjlr5b96c7w" />
                <figcaption className="mt-3 flex items-center justify-center gap-1.5 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-404" data-mp="3d28mziewcqxq">
                  {screen.label}
                  {index < PROOF_SCREENS.length - 1 && <ArrowRightIcon className="h-3 w-3 text-[var(--brand)]" aria-hidden="true" data-id="element-405" data-mp="33am2evgo5fib" />}
                </figcaption>
              </motion.figure>)}
          </div>
        </motion.div>
      </div>
    </InteriorSlide>;
}